<template>
  <div class="lg:container lg:mx-auto overflow-hidden">
    <Head :title="project.title" />
    <div class="flex flex-row justify-around">
      <div class="flex-auto pr-4 items-center">
        <h1 class="mb-4 font-bold text-2xl">Quote #{{ project.quote }}</h1>
        <span class="text-sm">Project</span>
        <div class="flex items-center mb-4">
          <h2 class="font-bold text-xl">{{ project.title }}</h2>
          <Link v-if="project.permissions.project.update" :href="`/projects/${project.id}/edit`" class="p-3 cursor-pointer">
            <icon name="edit" class="flex-shrink-0 w-4 h-4 fill-blue-400" />
          </Link>
        </div>
      </div>
      <div class="flex-auto flex items-center justify-end">
        <share
          :model-value="form.userSearch"
          :project="project"
          :users="users"
          :company="company"
          :permissions="permissions"
          @update:model-value="form.userSearch = $event"
          @reset="form.userSearch = null"
        />
      </div>
      <div class="flex-auto flex items-center ml-2">
        <SendMail
          :project="project"
          :users="users"
          :company="company"
          :permissions="permissions"
        />
      </div>
    </div>
    <!-- Title -->

    <div class="max-w-3xl md:flex md:mb-4">
      <div class="md:w-3/4">
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Project Address</p>
          <p class="text-gray-400">{{ project.address }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">City</p>
          <p class="text-gray-400">{{ project.city }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">State/Region</p>
          <p class="text-gray-400">{{ project.state }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Country</p>
          <p class="text-gray-400">{{ project.country }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Zip/Post code</p>
          <p class="text-gray-400">{{ project.zipcode }}</p>
        </div>
      </div>
      <div class="md:w-3/4">
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Created:</p>
          <p class="text-gray-400">{{ project.created_at }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Last Edit:</p>
          <p class="text-gray-400">{{ project.updated_at }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Total length:</p>
          <p class="text-gray-400">{{ project.length }} (m)</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Total Parts:</p>
          <p class="text-gray-400">{{ project.parts_count }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Total Weight:</p>
          <p class="text-gray-400">{{ project.weight }}</p>
        </div>
      </div>
      <div class="md:w-2/4">
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Name:</p>
          <p class="text-gray-400">{{ project.dealer.name }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Email:</p>
          <p class="text-gray-400">{{ project.dealer.email }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Phone:</p>
          <p class="text-gray-400">{{ project.dealer.phone }}</p>
        </div>
      </div>
    </div>
    <!-- Project Summary -->

    <!-- Tabs -->
    <div class="flex items-center justify-between border-solid border-b border-blue-800 mb-8">
      <ul class="flex flex-wrap w-1/2">
        <li class="flex-auto mr-0.5">
          <a
            :href="'/projects/' +project.id+'/' + 1"
            class="block px-4 py-4 w-full hover:bg-orange-400 border border-b-0 border-transparent"
            :class="tab == tabs.parts ? 'bg-blue-800 text-white' : ' bg-white text-blue-800 border-blue-800' "
          >
            Project Summary
          </a>
        </li>
        <li class="flex-auto mr-0.5">
          <a
            :href="'/projects/' +project.id+'/' + 2"
            class="block px-4 py-4 w-full hover:bg-orange-400 border border-b-0 border-transparent"
            :class="tab == tabs.runs ? 'bg-blue-800 text-white' : ' bg-white text-blue-800 border-blue-800' "
          >
            Project Runs
          </a>
        </li>
      </ul>

      <div class="flex items-center mb-4 md:mb-0 flex-wrap">
        <Link
          v-if="project.permissions.run.create && project.permissions.run.custom"
          class="btn-blue h-10 bg-white border-2 border-solid border-blue-600 mr-4 text-blue-800"
          :href="`/projects/${project.id}/customs/create`"
        >
          <span> + New Custom Run</span>
        </Link>
        <Link
          v-if="project.permissions.run.create" class="btn-blue h-10"
          :href="`/projects/${project.id}/runs/create`"
        >
          <span> + New Run</span>
        </Link>
      </div>
    </div>

    <div v-if="tab == tabs.parts">
      <div class="flex flex-row justify-between aligns-center">
        <div class="w-full">
          <ul class="flex flex-row items-center">
            <li v-if="project.permissions.project.status" class="m-1">
              <select-input :model-value="project.status" class="p-0" @update:model-value="updateStatus($event)">
                <option :value="null" selected>Status</option>
                <option v-for="(v,k) in statuses" :key="k" :value="k"> {{ v }}</option>
              </select-input>
            </li>
            <li class="m-1">
              <div v-if="project.permissions.run.create" class="flex flex-row items-center">
                <select id="discount-multiplier" v-model="discount" name="discount-multiplier" class="form-select w-full" @change="overrideDiscount()">
                  <option :value="null" selected>Discount Multiplier</option>
                  <option v-for="d in company.discounts" :key="d.id" :value="d.id">{{ d.value }}</option>
                </select>
                <button class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="discount = null, overrideDiscount()">X</button>
              </div>
            </li>
            <li class="mx-1">
              <download-bulk-button v-if="project.permissions.project.view" :project="project.id" :runs="[]" :label="true" class="w-full" />
            </li>
            <li v-if="! project.deleted_at && project.permissions.project.delete" class="mx-1">
              <button class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="projectDestroy">Delete Project</button>
            </li>
            <li v-if="project.deleted_at && project.permissions.project.restore" class="mx-1">
              <button class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="projectRestore">Restore Project</button>
            </li>
          </ul>
        </div>
      </div>
      <!-- Bulk Actions -->

      <div class="overflow-x-auto mb-12">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left font-bold text-lg">
              <th class="pt-2 pr-4">Part</th>
              <th class="pt-2 pr-4">Description</th>
              <th class="pt-2 px-4 text-center">Price</th>
              <th class="pt-2 px-4 text-center">Quantity</th>
              <th class="pt-2 text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(part, index) in parts" :key="index"
              class="hover:bg-gray-100 focus-within:bg-gray-100"
              :class="index % 2 == 0 ? '' : 'bg-gray-100'"
            >
              <td>
                <span class="flex items-center p-2 focus:text-blue-500">
                  {{ part.part_number }}
                  <icon v-if="part.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
                </span>
              </td>
              <td>
                {{ part.description }}
              </td>
              <td class="text-right">
                ${{ part.price }}
              </td>
              <td class="text-center">
                {{ part.qty }}
              </td>
              <td class="text-right font-bold">
                $ {{ part.total_str }}
              </td>
            </tr>
            <tr v-if="project.parts?.length === 0">
              <td class="px-6 py-4 border-t text-center text-xl" colspan="6">No parts found.</td>
            </tr>
          </tbody>
          <tfoot v-if="project.total">
            <tr class="border-t">
              <td class="text-right" colspan="3">&nbsp;</td>
              <td class="text-center  pt-2 ">Total</td>
              <td class="text-right font-bold  pt-2">$ {{ project.total }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
    <!-- Project Parts -->

    <div v-if="tab == tabs.runs">
      <div class="flex items-center justify-between flex-wrap mb-10">
        <div class="flex">
          <ul v-if="selected.length > 0 && runs.length" class="text-center md:flex md:items-center">
            <li class="m-1">
              <div v-if="runs[0].permissions.update && form.trashed !=='only'" class="flex flex-row items-center">
                <select id="discount-multiplier" v-model="discount" name="discount-multiplier" class="form-select w-full" @change="bulkDiscount()">
                  <option :value="null" selected>Discount Multiplier</option>
                  <option v-for="d in company.discounts" :key="d.id" :value="d.id">{{ d.value }}</option>
                </select>
                <button class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="discount = null, bulkDiscount()">X</button>
              </div>
            </li>
            <li class="m-1">
              <a v-if="project.permissions.run.create && form.trashed !=='only'" class="block md:inline btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="bulkClone()">Duplicate</a>
            </li>
            <li class="m-1">
              <a v-if="runs[0].permissions.delete && form.trashed !=='only'" class="block md:inline btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="bulkDestroy()">Delete</a>
            </li>
            <li class="m-1">
              <a v-if="runs[0].permissions.restore && form.trashed === 'only'" class="block md:inline btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="bulkRestore()">Restore</a>
            </li>
            <li class="m-1">
              <download-bulk-button v-if="runs[0].permissions.view && form.trashed !=='only'" :project="project.id" :runs="selected" :label="true" class="w-full" :quote="false" :csv="false" />
            </li>
          </ul>
        </div>
        <div class="flex items-center justify-between">
          <search-filter v-model="form.search" class="mr-4 w-full" @reset="reset">
            <label class="block text-gray-700">Deleted:</label>
            <select v-model="form.trashed" class="form-select mt-1 w-full">
              <option :value="null">Exclude Deleted</option>
              <option value="with">Include Deleted</option>
              <option value="only">Only Deleted</option>
            </select>
          </search-filter>
        </div>
      </div>
      <!-- Bulk Actions -->

      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left font-bold text-lg">
              <th class="pt-2 px-4 text-center">
                <input v-show="runs.length" type="checkbox" :checked="isSelected" @click="toggle" />
              </th>
              <th class="pt-2 pr-4">System</th>
              <th class="pt-2 pr-4">Run Name</th>
              <th class="pt-2 px-4 text-center">Feet</th>
              <th class="pt-2 px-4 text-center">Meters</th>
              <th class="pt-2 px-4 text-center">Discount Multiplier</th>
              <th class="pt-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(run, index) in runs" :key="run.id"
              class="hover:bg-gray-100 focus-within:bg-gray-100"
              :class="index % 2 == 0 ? '' : 'bg-gray-100'"
            >
              <td class="text-center">
                <input v-model="selected" type="checkbox" :value="run.id" />
              </td>
              <td>
                <Link class="flex items-center p-2 focus:text-blue-500" :href="`/runs/${run.id}/edit`" title="View/Edit Drawing">
                  {{ run.system }}
                  <icon v-if="run.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
                </Link>
              </td>
              <td>
                <Link class="flex items-center p-2 focus:text-blue-500" :href="`/runs/${run.id}/edit`" title="View/Edit Drawing">
                  {{ run.name }}
                  <icon v-if="run.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
                </Link>
              </td>
              <td class="text-center">
                {{ run.feet }}
              </td>
              <td class="text-center">
                {{ run.length }}
              </td>
              <td class="text-center">
                {{ run.discount }}
              </td>
              <td class="flex items-center">
                <download-button v-if="! run.deleted_at && run.permissions.view" :run-id="run.id" />
                <Link v-if="! run.deleted_at && run.permissions.view" class="p-2" :href="`/runs/${run.id}`" title="View Run Details">
                  <icon name="eye" class="block w-4 h-4" />
                </Link>
                <Link v-if="! run.deleted_at && run.permissions.update" class="p-2" :href="`/runs/${run.id}/edit`" tabindex="-1" title="View/Edit Drawing">
                  <icon name="edit" class="block w-4 h-4" />
                </Link>
                <button v-if="! run.deleted_at && run.permissions.create" class="p-2" type="button" title="Clone Run" @click="clone(run.id)">
                  <icon name="clone" class="block w-4 h-4" />
                </button>
                <button v-if="! run.deleted_at && run.permissions.delete" class="p-2" type="button" title="Delete Run" @click="destroy(run.id)">
                  <icon name="trash" class="block w-4 h-4" />
                </button>
                <button v-if="run.deleted_at && run.permissions.restore" class="p-2" type="button" title="Restore Run" @click="restore(run.id)">
                  <icon name="trash" class="block w-4 h-4" />
                </button>
              </td>
            </tr>
            <tr v-if="runs.length === 0">
              <td class="px-6 py-4 border-t text-center text-xl" colspan="6">No runs found.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Table -->
    </div>
    <!-- Project Runs -->

    <div class="mt-10">
      <div class="flex items-center border-b pb-2">
        <h4 class="font-bold text-lg mr-4">Project Notes</h4>
        <create-note v-if="project.permissions.notes.create" :project="project" />
      </div>

      <table class="w-full text-left mb-12">
        <thead>
          <tr>
            <th class="py-3 w-3/5">Note</th>
            <th class="py-3">Author</th>
            <th class="py-3">Updated</th>
            <th class="py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="note in notes" :key="note.id">
            <td class="border-t py-2">
              <a v-if="note.permissions.update" class="flex items-center focus:text-blue-500 cursor-pointer" :class="{'line-through': note.deleted_at}" @click="noteToEdit = note">
                {{ note.description }}
                <icon v-if="note.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </a>
              <span v-else>{{ note.description }}</span>
            </td>
            <td class="border-t py-2">
              {{ note.user }}
            </td>
            <td class="border-t py-2">
              {{ note.updated_at }}
            </td>
            <td class="border-t py-2">
              <div class="flex items-center">
                <a v-if="note.permissions.update" class="p-2 cursor-pointer" @click="noteToEdit = note"><icon name="edit" class="block w-4 h-4" /></a>
                <button v-if="! note.deleted_at && note.permissions.delete" class="p-2" type="button" @click="destroyNote(note.id)">
                  <icon name="trash" class="block w-4 h-4" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <edit-note v-if="noteToEdit && noteToEdit.permissions.update" class="p-2" :note="noteToEdit" @close="noteToEdit = null" />
    </div>
    <!-- Notes -->
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import SelectInput from '@/Shared/SelectInput'
import DownloadBulkButton from '../Runs/Partials/DownloadBulkButton'
import DownloadButton from '../Runs/Partials/DownloadButton'
import Share from './Partials/Share'
import SendMail from './Partials/SendMail'
import CreateNote from './../Notes/Create'
import EditNote from './../Notes/Edit'

export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    SelectInput,
    DownloadBulkButton,
    DownloadButton,
    Share,
    SendMail,
    CreateNote,
    EditNote,
  },
  layout: Layout,
  props: {
    owner      : Boolean,
    filters    : Object,
    statuses   : Object,
    project    : Object,
    runs       : Object,
    parts      : Object,
    notes      : Object,
    users      : Array,
    company    : Object,
    permissions: Object,
    tab: Number,
  },
  data() {
    return {
      selected: [],
      discount: this.project.discount,
      noteToEdit: null,
      form: {
        search    : this.filters.search,
        trashed   : this.filters.trashed,
        userSearch: this.filters.userSearch,
      },
    }
  },
  computed: {
    tabs: () => ({
      parts: 1,
      runs: 2,
    }),
    isSelected() {
      return this.selected.length > 0
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(`/projects/${this.project.id}/${this.tab}`, pickBy(this.form), { preserveState: true })
      }, 700),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    projectDestroy() {
      if (confirm('Are you sure you want to delete this project?')) {
        this.$inertia.delete(`/projects/${this.project.id}`)
      }
    },
    projectRestore() {
      if (confirm('Are you sure you want to restore this project?')) {
        this.$inertia.put(`/projects/${this.project.id}/restore`)
      }
    },
    toggle() {
      if (this.selected.length > 0) {
        this.selected = []
      } else {
        this.selected = []
        this.selected = this.runs.map(el => el.id)
      }
    },
    clone(id) {
      if (confirm('Are you sure you want to duplicate this run?')) {
        this.$inertia.put(`/runs/${id}/duplicate`, {
          onSuccess: () => {},
        })
      }
    },
    destroy(id) {
      if (confirm('Are you sure you want to delete this run?')) {
        this.$inertia.delete(`/runs/${id}`, {
          onSuccess: () => {},
        })
      }
    },
    restore(id) {
      if (confirm('Are you sure you want to restore this run?')) {
        this.$inertia.put(`/runs/${id}/restore`, {
          onSuccess: () => {},
        })
      }
    },
    updateStatus (status) {
      if (confirm('Are you sure you want to update the project status to ' + this.statuses[status]  +' ?')) {
        this.$inertia.put(`/projects/${this.project.id}/status/${status}`)
      }
    },
    applyDiscount () {
      this.$inertia.put(`/projects/bulk/${this.project.id}/discount`, {
        runs    : this.selected,
        discount: this.discount,
      })
    },
    bulkDiscount() {
      let c = true
      if (this.company.discounts.filter(d => d.id == 0).length > 0) {
        c = confirm('Are you sure you want to set the discount for all runs.')
      }

      if (c) {
        this.applyDiscount()
      }
    },
    overrideDiscount() {
      let c = true
      if (this.company.discounts.filter(d => d.id == 0).length > 0) {
        c = confirm('Are you sure you want to override the discount for all runs.')
      }

      if (c) {
        this.applyDiscount()
      }
    },
    bulkClone() {
      if (confirm('Are you sure you want to duplicate all selected runs?')) {
        this.$inertia.put(`/projects/bulk/${this.project.id}/duplicate`, { runs: this.selected },{
          onSuccess: () => {},
        })
      }
    },
    bulkDestroy() {
      if (confirm('Are you sure you want to delete all selected runs?')) {
        this.$inertia.put(`/projects/bulk/${this.project.id}/destroy`, { runs: this.selected },{
          onSuccess: () => {},
        })
      }
    },
    bulkRestore() {
      if (confirm('Are you sure you want to restore all selected runs?')) {
        this.$inertia.put(`/projects/bulk/${this.project.id}/restore`, { runs: this.selected },{
          onSuccess: () => {},
        })
      }
    },
    destroyNote(id) {
      if (confirm('Are you sure you want to delete this note?')) {
        this.$inertia.delete(`/notes/${id}`, {
          onSuccess: () => {},
        })
      }
    },
  },
}
</script>
